import { useState } from "react";
import { HStack, IconButton, Button, Tooltip, useToast } from "@chakra-ui/react";
import { FiThumbsUp, FiThumbsDown, FiRefreshCw } from "react-icons/fi";
import { useLunaStore, useLunaApi } from "state/luna";

interface LunaMessageActionsProps {
  sessionId: string;
  messageId: string;
  rating?: "good" | "bad" | null;
  onRetry?: () => void;
}

export const LunaMessageActions = ({
  sessionId,
  messageId,
  rating,
  onRetry,
}: LunaMessageActionsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasRated, setHasRated] = useState(!!rating);
  const [currentRating, setCurrentRating] = useState<"good" | "bad" | null>(
    rating || null,
  );
  const optimisticRateMessage = useLunaStore((state) => state.rateMessage);
  const { rateMessage } = useLunaApi();
  const toast = useToast();

  const handleRate = async (newRating: "good" | "bad") => {
    if (hasRated || isLoading) return;

    setIsLoading(true);
    // Optimistic update
    setCurrentRating(newRating);
    optimisticRateMessage(sessionId, messageId, newRating);

    try {
      await rateMessage(sessionId, messageId, newRating);
      setHasRated(true);
      toast({
        title: "Feedback recorded",
        description: "Thank you for your feedback!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      // Revert on failure
      setCurrentRating(rating || null);
      toast({
        title: "Failed to submit feedback",
        description:
          error instanceof Error ? error.message : "Unknown error occurred",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <HStack justify="flex-end" spacing="sm">
      <Tooltip label="Good response" placement="top">
        <IconButton
          aria-label="Good response"
          icon={<FiThumbsUp />}
          size="sm"
          variant="ghost"
          color={currentRating === "good" ? "success.500" : "text.body.tertiary"}
          onClick={() => handleRate("good")}
          isDisabled={hasRated || isLoading}
          _hover={!hasRated ? { bg: "success.50", color: "success.500" } : {}}
        />
      </Tooltip>

      <Tooltip label="Bad response" placement="top">
        <IconButton
          aria-label="Bad response"
          icon={<FiThumbsDown />}
          size="sm"
          variant="ghost"
          color={currentRating === "bad" ? "error.500" : "text.body.tertiary"}
          onClick={() => handleRate("bad")}
          isDisabled={hasRated || isLoading}
          _hover={!hasRated ? { bg: "error.50", color: "error.500" } : {}}
        />
      </Tooltip>

      {onRetry && (
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<FiRefreshCw />}
          color="text.body.tertiary"
          onClick={onRetry}
          _hover={{ bg: "bg.hover" }}
        >
          Retry
        </Button>
      )}
    </HStack>
  );
};
